import { useContext } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Box, Button, CircularProgress } from "@mui/material";

import { useConfirmationDialogContext } from "../../../../confirmation-dialog/ConfirmationDialogContext";
import { FormDialog } from "../../../../form-dialog/FormDialog";
import { ClientFileEntryKind, ClientFileInfoLineContext } from "../../../context/ClientFileInfoLineContext";
import { ClaimFormFields } from "../claim-form-fields/ClaimFormFields";
import { useClaimEdit } from "./hooks/useClaimEdit";

interface Props {
  clientId: string;
  claimId: string;
  onClose: () => void;
}

export function ClaimEditDialog({ clientId, claimId, onClose }: Props) {
  const { t } = useTranslation();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const renderInfoLine = useContext(ClientFileInfoLineContext);
  const {
    formik,
    options,
    claim,
    isLoading,
    isInactiveRepresentativeSelected,
    handleMarkAsCompleted,
    handleMarkAsOpen,
    handleDelete,
    isDeleting,
  } = useClaimEdit(clientId, claimId, onClose);

  const infoLine = renderInfoLine?.({ kind: ClientFileEntryKind.Claim, id: claimId });

  const confirmDelete = () => {
    if (!claim) {
      return;
    }
    showConfirmationDialog({
      dialogTitleText: t("claims.delete.dialogTitle"),
      dialogText: (
        <Trans
          i18nKey="claims.delete.dialogText"
          values={{ creditorName: claim.creditorName }}
          components={{ strong: <strong /> }}
        />
      ),
      agreeText: t("claims.delete.confirm"),
      agreeColor: "error",
      discardText: t("claims.delete.cancel"),
      onClose: (confirmed) => {
        if (confirmed) {
          handleDelete();
        }
      },
    });
  };

  return (
    <FormDialog
      title={t("claims.edit.dialogTitle")}
      onClose={onClose}
      formik={formik}
      submitLabel={t("common.save")}
      submitDisabled={!formik.dirty || isLoading || isInactiveRepresentativeSelected}
      extraAction={
        claim
          ? {
              left: (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={confirmDelete}
                  disabled={isDeleting || formik.isSubmitting}
                >
                  {t("claims.edit.delete")}
                </Button>
              ),
              right: claim.isCompleted ? (
                <Button
                  variant="outlined"
                  onClick={() => void handleMarkAsOpen()}
                  disabled={isDeleting || formik.isSubmitting}
                >
                  {t("claims.edit.markAsOpen")}
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  onClick={() => void handleMarkAsCompleted()}
                  disabled={isDeleting || formik.isSubmitting}
                >
                  {t("claims.edit.markAsCompleted")}
                </Button>
              ),
            }
          : undefined
      }
      aboveActions={infoLine}
    >
      {isLoading || !claim ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <ClaimFormFields
          formik={formik}
          options={options}
          clientId={clientId}
          editData={{
            creditor: {
              id: claim.creditorId,
              displayName: claim.creditorName,
              isClientSpecific: claim.isCreditorClientSpecific,
            },
            representative: {
              id: claim.representativeId ?? undefined,
              displayName: claim.representativeName ?? undefined,
              isClientSpecific: claim.isRepresentativeClientSpecific ?? false,
            },
            hasInactiveRepresentative: isInactiveRepresentativeSelected,
          }}
        />
      )}
    </FormDialog>
  );
}
